/**
 * SignupWizard — organisation-centric 5-step registration (/register).
 * Pathways: solo | business | employee | council. Invite links (?invite=)
 * keep the short single-step flow. Ends with email verification (soft gate).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, User, Briefcase, Building2, Landmark, Users, ArrowLeft, ArrowRight,
  Search, ShieldCheck, MailCheck, AlertTriangle, CheckCircle2, Pencil,
} from "lucide-react";

const MODULES = [
  { id: "pool_spa", label: "Pool & Spa" },
  { id: "health", label: "Health" },
  { id: "building", label: "Building Compliance" },
  { id: "pest", label: "Pest & Building" },
  { id: "certification", label: "Certification" },
  { id: "fire", label: "Fire Safety" },
];
const DISCIPLINES = ["Private certifier", "Pool inspector", "Building certifier", "Building inspector",
  "Fire safety professional", "Health inspector", "Pest/building inspector", "Other compliance professional"];
const TEAM_SIZES = ["1", "2-5", "6-10", "11-25", "26-50", "51+"];
const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const PATHWAYS = [
  { id: "solo", icon: User, title: "I work for myself",
    hint: "Sole trader · independent certifier or inspector · one-person business" },
  { id: "business", icon: Briefcase, title: "I own or manage a business",
    hint: "Certification company · inspection business · consultancy · compliance company" },
  { id: "employee", icon: Users, title: "I work for an organisation",
    hint: "Employee · inspector · team member of an existing business" },
  { id: "council", icon: Landmark, title: "Council or government",
    hint: "Local council · government department · regulatory authority" },
];
const DRAFT_KEY = "ch_signup_draft";

const track = (event, pathway = "", step = "") => {
  api.post("/signup/track", { event, pathway, step,
    anon_id: localStorage.getItem("ch_anon_id") || "" }).catch(() => {});
};

const WInput = ({ label, value, onChange, testid, type = "text", required, placeholder = "", autoComplete, inputMode, optional }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
      {label}{optional && <em className="text-slate-400 normal-case font-medium"> — optional</em>}
    </span>
    <input type={type} value={value} required={required} placeholder={placeholder}
      autoComplete={autoComplete} inputMode={inputMode} data-testid={testid}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full h-12 border-2 border-slate-300 px-3 text-[15px] bg-white text-slate-900 focus:border-[#1E1B4B] focus:outline-none"/>
  </label>
);

export default function SignupWizard() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const navigate = useNavigate();
  const { registerV2, refresh, logout } = useAuth();

  const saved = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}"); } catch { return {}; }
  }, []);

  const [step, setStep] = useState(saved.step || 1); // 1..5, 6=verify
  const [busy, setBusy] = useState(false);
  const [personal, setPersonal] = useState(saved.personal || {
    first_name: "", last_name: "", email: "", mobile: "", over_18: false, accept_terms: false });
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [pathway, setPathway] = useState(saved.pathway || "");
  const [org, setOrg] = useState(saved.org || {
    name: "", legal_name: "", abn: "", business_email: "", business_phone: "",
    address: "", state: "", postcode: "", website: "", team_size: "1",
    disciplines: [], job_title: "", department: "" });
  const [modules, setModules] = useState(saved.modules || []);
  const [joinOrg, setJoinOrg] = useState(saved.joinOrg || null); // {org_id, name}
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [duplicates, setDuplicates] = useState(null);
  const [joinPending, setJoinPending] = useState(false);

  // invite mode
  const [invitePreview, setInvitePreview] = useState(null);
  const [inviteError, setInviteError] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("ch_anon_id")) localStorage.setItem("ch_anon_id", crypto.randomUUID());
    track("signup_started");
  }, []);

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/team/invite/lookup/${inviteToken}`)
      .then((r) => {
        setInvitePreview(r.data);
        setPersonal((p) => ({ ...p, email: r.data.email }));
      })
      .catch((e) => setInviteError(e?.response?.data?.detail || "This invitation link is no longer valid."));
  }, [inviteToken]);

  useEffect(() => {
    if (step >= 6) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, personal, pathway, org, modules, joinOrg }));
  }, [step, personal, pathway, org, modules, joinOrg]);

  const upP = (k, v) => setPersonal((p) => ({ ...p, [k]: v }));
  const upO = (k, v) => setOrg((o) => ({ ...o, [k]: v }));

  const validStep1 = () => {
    if (!personal.first_name.trim() || !personal.last_name.trim()) return "Please enter your first and last name";
    if (!/^\S+@\S+\.\S+$/.test(personal.email)) return "Please enter a valid work email address";
    if (password.length < 10) return "Password must be at least 10 characters";
    if (password !== password2) return "Passwords do not match";
    if (!personal.over_18) return "Please confirm you are 18 years of age or older";
    if (!personal.accept_terms) return "Please accept the Terms of Service and Privacy Policy";
    return null;
  };

  const doSearch = async (q) => {
    setSearching(true);
    try {
      const domain = personal.email.includes("@") ? personal.email.split("@")[1] : "";
      const r = await api.get("/orgs/search", { params: { q, domain } });
      setSearchResults(r.data.results || []);
    } catch { setSearchResults([]); }
    setSearching(false);
  };

  const submit = async (ack = false) => {
    setBusy(true);
    setDuplicates(null);
    try {
      const payload = {
        ...personal, password, pathway: pathway || "solo",
        organisation: org, modules,
        join_org_id: joinOrg?.org_id || null,
        duplicate_ack: ack,
        invite_token: inviteToken || null,
      };
      const res = await registerV2(payload);
      localStorage.removeItem(DRAFT_KEY);
      setJoinPending(!!res.join_request_pending);
      setStep(6);
      toast.success("Account created — check your email for the verification code");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.duplicates) {
        setDuplicates(detail.duplicates);
      } else {
        toast.error(typeof detail === "string" ? detail : "Signup failed — please check your details");
      }
    } finally { setBusy(false); }
  };

  // ------------------------------------------------- invite short flow
  if (inviteToken) {
    return (
      <Shell title={invitePreview ? "Join your team" : "Team invitation"} step={null}>
        {inviteError && <div className="p-4 border-2 border-red-200 bg-red-50 text-sm text-red-900" data-testid="invite-error">{inviteError}</div>}
        {!invitePreview && !inviteError && <div className="p-4 text-slate-500 text-sm flex gap-2" data-testid="invite-loading"><Loader2 size={16} className="animate-spin"/>Checking invitation…</div>}
        {invitePreview && (
          <div className="space-y-5">
            <div className="p-4 border-2 border-teal-200 bg-teal-50 text-sm" data-testid="invite-banner">
              <strong>{invitePreview.owner_name}</strong> invited you to join <strong>{invitePreview.owner_company || `${invitePreview.owner_name}'s team`}</strong>. You'll share their <strong className="uppercase">{invitePreview.tier}</strong> plan.
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <WInput label="First name" value={personal.first_name} onChange={(v) => upP("first_name", v)} testid="su-first-name" required autoComplete="given-name"/>
              <WInput label="Last name" value={personal.last_name} onChange={(v) => upP("last_name", v)} testid="su-last-name" required autoComplete="family-name"/>
            </div>
            <WInput label="Email" value={personal.email} onChange={() => {}} testid="su-email" type="email"/>
            <WInput label="Mobile" value={personal.mobile} onChange={(v) => upP("mobile", v)} testid="su-mobile" type="tel" inputMode="tel" autoComplete="tel" optional/>
            <div className="grid sm:grid-cols-2 gap-4">
              <WInput label="Password (min 10 chars)" value={password} onChange={setPassword} testid="su-password" type="password" required autoComplete="new-password"/>
              <WInput label="Confirm password" value={password2} onChange={setPassword2} testid="su-password2" type="password" required autoComplete="new-password"/>
            </div>
            <Consents personal={personal} upP={upP}/>
            <button onClick={() => { const err = validStep1(); if (err) { toast.error(err); return; } submit(); }}
              disabled={busy} data-testid="su-invite-submit"
              className="w-full h-14 bg-[#1E1B4B] text-white font-bold text-lg hover:bg-[#0F0E2E] disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={18} className="animate-spin"/> : null}Join team
            </button>
          </div>
        )}
        {step === 6 && null}
      </Shell>
    );
  }

  // ------------------------------------------------- verify step
  if (step === 6) return <VerifyStep joinPending={joinPending} email={personal.email} refresh={refresh} navigate={navigate} logout={logout}/>;

  // ------------------------------------------------- duplicate warning overlay
  if (duplicates) {
    return (
      <Shell title="Possible existing organisation" step={null}>
        <div data-testid="su-duplicates">
          <div className="p-4 border-2 border-amber-300 bg-amber-50 text-sm text-amber-900 flex gap-2 mb-4">
            <AlertTriangle size={18} className="shrink-0 mt-0.5"/>
            <span>We found an organisation that may already match your business. To avoid duplicate accounts (and keep your team together), you can request access instead.</span>
          </div>
          <div className="space-y-2 mb-5">
            {duplicates.map((d) => (
              <div key={d.org_id} className="border-2 border-slate-200 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`su-duplicate-${d.org_id}`}>
                <div>
                  <p className="font-black text-slate-900">{d.name}</p>
                  <p className="text-xs text-slate-500">{d.match_reason}{d.state ? ` · ${d.state}` : ""}</p>
                </div>
                <button onClick={() => { setJoinOrg({ org_id: d.org_id, name: d.name }); setDuplicates(null); submitJoin(d); }}
                  data-testid={`su-duplicate-join-${d.org_id}`}
                  className="h-10 px-4 border-2 border-teal-600 text-teal-700 font-bold text-xs hover:bg-teal-50">
                  Request access
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => setDuplicates(null)} data-testid="su-duplicates-back"
              className="h-12 px-5 border-2 border-slate-300 font-bold text-sm text-slate-700 hover:bg-slate-50">← Back and edit details</button>
            <button onClick={() => submit(true)} disabled={busy} data-testid="su-duplicates-different"
              className="h-12 px-5 bg-[#1E1B4B] text-white font-bold text-sm hover:bg-[#0F0E2E] disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin inline mr-1"/> : null}This is a different organisation — continue
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  function submitJoin(dup) {
    // Re-submit as a join request against the matched org.
    setBusy(true);
    registerV2({ ...personal, password, pathway: pathway === "council" ? "council" : "employee",
      organisation: org, modules, join_org_id: dup.org_id, duplicate_ack: false, invite_token: null })
      .then((res) => { localStorage.removeItem(DRAFT_KEY); setJoinPending(!!res.join_request_pending); setStep(6); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Signup failed"))
      .finally(() => setBusy(false));
  }

  const stepLabels = ["About You", "How You Work", "Organisation", "Modules", "Review"];
  const joiningExisting = pathway === "employee" || (pathway === "council" && joinOrg);

  return (
    <Shell title="Create your account" step={step} labels={stepLabels}>
      {step === 1 && (
        <div className="space-y-4" data-testid="su-step-1">
          <div className="grid sm:grid-cols-2 gap-4">
            <WInput label="First name" value={personal.first_name} onChange={(v) => upP("first_name", v)} testid="su-first-name" required autoComplete="given-name"/>
            <WInput label="Last name" value={personal.last_name} onChange={(v) => upP("last_name", v)} testid="su-last-name" required autoComplete="family-name"/>
          </div>
          <WInput label="Work email" value={personal.email} onChange={(v) => upP("email", v)} testid="su-email" type="email" required autoComplete="email" inputMode="email"/>
          <WInput label="Mobile number" value={personal.mobile} onChange={(v) => upP("mobile", v)} testid="su-mobile" type="tel" inputMode="tel" autoComplete="tel"/>
          <div className="grid sm:grid-cols-2 gap-4">
            <WInput label="Password (min 10 chars)" value={password} onChange={setPassword} testid="su-password" type="password" required autoComplete="new-password"/>
            <WInput label="Confirm password" value={password2} onChange={setPassword2} testid="su-password2" type="password" required autoComplete="new-password"/>
          </div>
          <Consents personal={personal} upP={upP}/>
          <NavButtons onNext={() => { const err = validStep1(); if (err) { toast.error(err); return; } setStep(2); }}/>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3" data-testid="su-step-2">
          <p className="text-sm text-slate-600 font-bold">How will you use Compliance Hub?</p>
          {PATHWAYS.map((p) => {
            const Icon = p.icon;
            const active = pathway === p.id;
            return (
              <button key={p.id} onClick={() => { setPathway(p.id); setJoinOrg(null); track("pathway_selected", p.id); setStep(3); }}
                data-testid={`su-pathway-${p.id}`}
                className={`w-full text-left border-2 p-4 flex items-start gap-3 transition-colors ${active ? "border-[#1E1B4B] bg-indigo-50/50" : "border-slate-200 hover:border-slate-400"}`}>
                <Icon size={22} className={active ? "text-[#1E1B4B]" : "text-slate-400"} strokeWidth={2.2}/>
                <span>
                  <span className="block font-black text-slate-900">{p.title}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">{p.hint}</span>
                </span>
              </button>
            );
          })}
          <NavButtons onBack={() => setStep(1)}/>
        </div>
      )}

      {step === 3 && (pathway === "solo" || pathway === "business") && (
        <div className="space-y-4" data-testid="su-step-3-org">
          <WInput label={pathway === "solo" ? "Trading / business name" : "Business name"} value={org.name} onChange={(v) => upO("name", v)} testid="su-org-name" required autoComplete="organization"/>
          <WInput label="Legal entity name (if different)" value={org.legal_name} onChange={(v) => upO("legal_name", v)} testid="su-org-legal" optional/>
          <div className="grid sm:grid-cols-2 gap-4">
            <WInput label="ABN" value={org.abn} onChange={(v) => upO("abn", v)} testid="su-org-abn" inputMode="numeric" placeholder="11 digits" optional/>
            <WInput label="Business phone" value={org.business_phone} onChange={(v) => upO("business_phone", v)} testid="su-org-phone" type="tel" inputMode="tel" optional/>
          </div>
          <WInput label="Business email" value={org.business_email} onChange={(v) => upO("business_email", v)} testid="su-org-email" type="email" inputMode="email" optional/>
          <WInput label="Business address" value={org.address} onChange={(v) => upO("address", v)} testid="su-org-address" autoComplete="street-address" optional/>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">State</span>
              <select value={org.state} onChange={(e) => upO("state", e.target.value)} data-testid="su-org-state"
                className="mt-1 w-full h-12 border-2 border-slate-300 px-2 bg-white text-slate-900 text-[15px] focus:border-[#1E1B4B] focus:outline-none">
                <option value="">Select…</option>{STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
            <WInput label="Postcode" value={org.postcode} onChange={(v) => upO("postcode", v)} testid="su-org-postcode" inputMode="numeric" autoComplete="postal-code"/>
          </div>
          <WInput label="Website" value={org.website} onChange={(v) => upO("website", v)} testid="su-org-website" inputMode="url" optional/>
          {pathway === "business" && (
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Approximate team size</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {TEAM_SIZES.map((t) => (
                  <button key={t} onClick={() => upO("team_size", t)} data-testid={`su-team-size-${t}`}
                    className={`h-10 px-4 border-2 font-bold text-sm ${org.team_size === t ? "border-[#1E1B4B] bg-[#1E1B4B] text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}>{t}</button>
                ))}
              </div>
            </label>
          )}
          {pathway === "solo" && (
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">What best describes you? (choose any)</span>
              <div className="mt-1 flex flex-wrap gap-2">
                {DISCIPLINES.map((d) => {
                  const on = org.disciplines.includes(d);
                  return (
                    <button key={d} data-testid={`su-discipline-${d.replace(/\W+/g, "-").toLowerCase()}`}
                      onClick={() => upO("disciplines", on ? org.disciplines.filter((x) => x !== d) : [...org.disciplines, d])}
                      className={`h-10 px-3 border-2 text-xs font-bold ${on ? "border-teal-600 bg-teal-50 text-teal-800" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>{d}</button>
                  );
                })}
              </div>
            </label>
          )}
          <NavButtons onBack={() => setStep(2)} onNext={() => {
            if (!org.name.trim()) { toast.error(pathway === "solo" ? "Please enter your trading/business name" : "Please enter your business name"); return; }
            track("org_details_completed", pathway); setStep(4);
          }}/>
        </div>
      )}

      {step === 3 && (pathway === "employee" || pathway === "council") && (
        <div className="space-y-4" data-testid="su-step-3-join">
          <div className="grid sm:grid-cols-2 gap-4">
            <WInput label="Job title" value={org.job_title} onChange={(v) => upO("job_title", v)} testid="su-job-title" optional={pathway !== "council"}/>
            <WInput label="Department / team" value={org.department} onChange={(v) => upO("department", v)} testid="su-department" optional/>
          </div>
          <label className="block">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{pathway === "council" ? "Council / organisation name" : "Organisation name"}</span>
            <div className="mt-1 flex gap-2">
              <input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} data-testid="su-org-search"
                placeholder={pathway === "council" ? "e.g. Penrith City Council" : "e.g. ABC Certification"}
                className="flex-1 h-12 border-2 border-slate-300 px-3 text-[15px] bg-white text-slate-900 focus:border-[#1E1B4B] focus:outline-none"
                onKeyDown={(e) => e.key === "Enter" && doSearch(searchQ)}/>
              <button onClick={() => doSearch(searchQ)} disabled={searching || searchQ.trim().length < 3} data-testid="su-org-search-btn"
                className="h-12 px-4 bg-[#1E1B4B] text-white font-bold text-sm disabled:opacity-50 inline-flex items-center gap-1.5">
                {searching ? <Loader2 size={15} className="animate-spin"/> : <Search size={15}/>}Search
              </button>
            </div>
          </label>
          {searchResults !== null && (
            <div className="space-y-2" data-testid="su-search-results">
              {searchResults.length === 0 && (
                <div className="border-2 border-dashed border-slate-300 p-4 text-sm text-slate-600" data-testid="su-search-empty">
                  <p className="font-bold text-slate-800">No matching organisation found on Compliance Hub.</p>
                  <p className="mt-1">Ask your organisation owner to send you an invitation — or, if you're the first from your {pathway === "council" ? "council" : "organisation"}, set it up now:</p>
                  <button onClick={() => { upO("name", searchQ); setJoinOrg(null); setStep(3.5); }} data-testid="su-create-new-org"
                    className="mt-3 h-11 px-4 border-2 border-teal-600 text-teal-700 font-bold text-sm hover:bg-teal-50">
                    Set up {searchQ.trim() || "my organisation"} as a new workspace
                  </button>
                </div>
              )}
              {searchResults.map((r) => (
                <button key={r.org_id} onClick={() => { setJoinOrg({ org_id: r.org_id, name: r.name }); }}
                  data-testid={`su-search-result-${r.org_id}`}
                  className={`w-full text-left border-2 p-3 flex items-center justify-between ${joinOrg?.org_id === r.org_id ? "border-teal-600 bg-teal-50" : "border-slate-200 hover:border-slate-400"}`}>
                  <span>
                    <span className="block font-black text-slate-900">{r.name}</span>
                    <span className="block text-xs text-slate-500">{r.business_type === "council" ? "Council" : "Organisation"}{r.state ? ` · ${r.state}` : ""} — already on Compliance Hub</span>
                  </span>
                  {joinOrg?.org_id === r.org_id && <CheckCircle2 size={18} className="text-teal-600"/>}
                </button>
              ))}
            </div>
          )}
          {joinOrg && (
            <div className="p-3 border-2 border-teal-300 bg-teal-50 text-sm text-teal-900" data-testid="su-join-note">
              It looks like <strong>{joinOrg.name}</strong> may already use Compliance Hub. You'll submit a <strong>request to join</strong> — the organisation owner must approve it before you get access. No duplicate organisation will be created.
            </div>
          )}
          <NavButtons onBack={() => setStep(2)} onNext={() => {
            if (!joinOrg) { toast.error("Search for your organisation and select it — or set it up as a new workspace"); return; }
            track("org_details_completed", pathway); setStep(5);
          }} nextLabel="Continue to review"/>
        </div>
      )}

      {step === 3.5 && (
        <div className="space-y-4" data-testid="su-step-3-new-council">
          <p className="text-sm text-slate-600">Set up <strong>{org.name || "your organisation"}</strong> as a new workspace. You'll become the organisation owner.</p>
          <WInput label={pathway === "council" ? "Council / organisation name" : "Organisation name"} value={org.name} onChange={(v) => upO("name", v)} testid="su-org-name" required/>
          <div className="grid sm:grid-cols-2 gap-4">
            <WInput label="Phone" value={org.business_phone} onChange={(v) => upO("business_phone", v)} testid="su-org-phone" type="tel" optional/>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">State</span>
              <select value={org.state} onChange={(e) => upO("state", e.target.value)} data-testid="su-org-state"
                className="mt-1 w-full h-12 border-2 border-slate-300 px-2 bg-white text-slate-900 text-[15px] focus:border-[#1E1B4B] focus:outline-none">
                <option value="">Select…</option>{STATES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          {pathway !== "council" && <WInput label="ABN" value={org.abn} onChange={(v) => upO("abn", v)} testid="su-org-abn" inputMode="numeric" optional/>}
          <NavButtons onBack={() => setStep(3)} onNext={() => {
            if (!org.name.trim()) { toast.error("Please enter the organisation name"); return; }
            track("org_details_completed", pathway); setStep(4);
          }}/>
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="su-step-4">
          <p className="text-sm text-slate-600 font-bold">Which areas will you use Compliance Hub for?</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {MODULES.map((m) => {
              const on = modules.includes(m.id);
              return (
                <button key={m.id} onClick={() => setModules(on ? modules.filter((x) => x !== m.id) : [...modules, m.id])}
                  data-testid={`su-module-${m.id}`}
                  className={`h-12 px-4 border-2 font-bold text-sm text-left flex items-center justify-between ${on ? "border-[#1E1B4B] bg-indigo-50/60 text-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>
                  {m.label}{on && <CheckCircle2 size={16} className="text-teal-600"/>}
                </button>
              );
            })}
          </div>
          <NavButtons onBack={() => setStep(pathway === "employee" || (pathway === "council" && joinOrg) ? 3 : (org.name && !joinOrg && (pathway === "employee" || pathway === "council") ? 3.5 : 3))}
            onNext={() => {
              if (modules.length === 0) { toast.error("Select at least one module"); return; }
              track("modules_selected", pathway); setStep(5);
            }}/>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4" data-testid="su-step-5">
          {track("review_reached", pathway) || null}
          <ReviewRow label="Your account" onEdit={() => setStep(1)} testid="su-review-account">
            <p className="font-black text-slate-900">{personal.first_name} {personal.last_name}</p>
            <p className="text-sm text-slate-600">{personal.email}{personal.mobile ? ` · ${personal.mobile}` : ""}</p>
          </ReviewRow>
          {joiningExisting && joinOrg ? (
            <ReviewRow label="Joining organisation" onEdit={() => setStep(3)} testid="su-review-org">
              <p className="font-black text-slate-900">{joinOrg.name}</p>
              <p className="text-sm text-amber-700 font-bold">Access requires approval by the organisation owner</p>
            </ReviewRow>
          ) : (
            <ReviewRow label="Organisation" onEdit={() => setStep(3)} testid="su-review-org">
              <p className="font-black text-slate-900">{org.name}</p>
              {org.abn && <p className="text-sm text-slate-600">ABN: {org.abn}</p>}
              <p className="text-sm text-slate-600">
                {{ solo: "Sole trader", business: "Private company", council: "Council / government" }[pathway] || "Organisation"} · Your role: <strong>Organisation Owner</strong>
              </p>
            </ReviewRow>
          )}
          {modules.length > 0 && (
            <ReviewRow label="Modules" onEdit={() => setStep(4)} testid="su-review-modules">
              <p className="text-sm font-bold text-slate-800">{modules.map((m) => MODULES.find((x) => x.id === m)?.label).join(" · ")}</p>
            </ReviewRow>
          )}
          <NavButtons onBack={() => setStep(joiningExisting ? 3 : 4)} onNext={() => submit(false)} busy={busy}
            nextLabel={joiningExisting ? "Create account & request access" : "Create account"}/>
        </div>
      )}
    </Shell>
  );
}

const Consents = ({ personal, upP }) => (
  <div className="space-y-1 pt-1">
    <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
      <input type="checkbox" checked={personal.over_18} onChange={(e) => upP("over_18", e.target.checked)}
        className="h-6 w-6 shrink-0 accent-[#1E1B4B]" data-testid="su-over-18"/>
      <span className="text-sm text-slate-700">I confirm that I am 18 years of age or older.</span>
    </label>
    <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
      <input type="checkbox" checked={personal.accept_terms} onChange={(e) => upP("accept_terms", e.target.checked)}
        className="h-6 w-6 shrink-0 accent-[#1E1B4B]" data-testid="su-accept-terms"/>
      <span className="text-sm text-slate-700">
        I accept the <Link to="/terms" target="_blank" className="font-bold text-[#1E1B4B] underline">Terms of Service</Link> and the <Link to="/privacy" target="_blank" className="font-bold text-[#1E1B4B] underline">Privacy Policy</Link>.
      </span>
    </label>
  </div>
);

const NavButtons = ({ onBack, onNext, nextLabel = "Continue", busy }) => (
  <div className="flex gap-3 pt-2">
    {onBack && (
      <button onClick={onBack} data-testid="su-back"
        className="h-13 min-h-[52px] px-5 border-2 border-slate-300 font-bold text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5">
        <ArrowLeft size={15}/>Back
      </button>
    )}
    {onNext && (
      <button onClick={onNext} disabled={busy} data-testid="su-next"
        className="flex-1 h-13 min-h-[52px] px-5 bg-[#1E1B4B] text-white font-bold text-[15px] hover:bg-[#0F0E2E] disabled:opacity-60 inline-flex items-center justify-center gap-2">
        {busy ? <Loader2 size={16} className="animate-spin"/> : null}{nextLabel}<ArrowRight size={15}/>
      </button>
    )}
  </div>
);

const ReviewRow = ({ label, children, onEdit, testid }) => (
  <div className="border-2 border-slate-200 p-4 flex items-start justify-between gap-3" data-testid={testid}>
    <div>
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-1">{label}</p>
      {children}
    </div>
    <button onClick={onEdit} className="text-slate-400 hover:text-slate-900 shrink-0" data-testid={`${testid}-edit`}><Pencil size={15}/></button>
  </div>
);

const Shell = ({ title, step, labels = [], children }) => (
  <div className="min-h-screen bg-slate-100 flex items-start sm:items-center justify-center px-4 py-8">
    <div className="w-full max-w-2xl bg-white border-2 border-slate-900 shadow-xl">
      <div className="bg-[#1E1B4B] px-6 py-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-teal-300">Compliance Hub</p>
        <h1 className="text-white font-black text-2xl leading-tight">{title}</h1>
        {step && (
          <div className="mt-3">
            <p className="text-xs text-indigo-200 font-bold" data-testid="su-step-indicator">
              Step {Math.min(Math.ceil(step), 5)} of 5 — {labels[Math.min(Math.ceil(step), 5) - 1]}
            </p>
            <div className="mt-1.5 h-1.5 bg-white/15">
              <div className="h-full bg-teal-400 transition-all" style={{ width: `${(Math.min(Math.ceil(step), 5) / 5) * 100}%` }}/>
            </div>
          </div>
        )}
      </div>
      <div className="p-6">{children}
        <p className="text-center text-sm text-slate-500 mt-6">
          Already have an account? <Link to="/login" className="font-bold text-[#1E1B4B] underline" data-testid="su-link-login">Sign in</Link>
        </p>
      </div>
    </div>
  </div>
);

const VerifyStep = ({ joinPending, email, refresh, navigate }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  const startCooldown = () => {
    setCooldown(60);
    timer.current = setInterval(() => setCooldown((c) => { if (c <= 1) { clearInterval(timer.current); return 0; } return c - 1; }), 1000);
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code: code.trim() });
      await refresh();
      toast.success("Email verified — welcome to Compliance Hub!");
      navigate("/select-module");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email re-sent");
      startCooldown();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resend"); }
  };

  return (
    <Shell title="Verify your email" step={null}>
      <div className="space-y-5 text-center" data-testid="su-verify-step">
        <MailCheck size={40} className="mx-auto text-teal-600"/>
        <p className="text-sm text-slate-600">We've sent a <strong>6-digit code</strong> to <strong className="text-slate-900">{email}</strong>. Enter it below to activate your account.</p>
        {joinPending && (
          <div className="p-3 border-2 border-amber-300 bg-amber-50 text-sm text-amber-900 text-left" data-testid="su-join-pending-note">
            <strong>Join request sent.</strong> The organisation owner has been notified — you'll receive an email once your access is approved.
          </div>
        )}
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" data-testid="su-verify-code"
          className="w-52 mx-auto h-14 border-2 border-slate-300 text-center text-2xl font-black tracking-[0.4em] focus:border-[#1E1B4B] focus:outline-none"/>
        <button onClick={verify} disabled={busy || code.length !== 6} data-testid="su-verify-submit"
          className="w-full h-14 bg-[#1E1B4B] text-white font-bold text-lg hover:bg-[#0F0E2E] disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy ? <Loader2 size={18} className="animate-spin"/> : <ShieldCheck size={18}/>}Verify email
        </button>
        <div className="flex items-center justify-center gap-4 text-sm">
          <button onClick={resend} disabled={cooldown > 0} data-testid="su-verify-resend"
            className="font-bold text-teal-700 underline disabled:opacity-50 disabled:no-underline">
            {cooldown > 0 ? `Resend available in ${cooldown}s` : "Resend verification email"}
          </button>
          <button onClick={() => navigate("/select-module")} data-testid="su-verify-later"
            className="text-slate-500 underline">Verify later</button>
        </div>
      </div>
    </Shell>
  );
};
